import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { LucideAngularModule, BarChart3, RefreshCcw } from 'lucide-angular';
import { forkJoin } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { Contract, Loa, ReaContractAnalysisDetail, ReaReport, Vendor } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { ContractsService } from '../../contract/services/contracts.service';
import { LoaService } from '../../loa/services/loa.service';
import { ReaReportsService } from '../../rea-reports/services/rea-reports.service';
import { VendorsService } from '../../vendor/services/vendors.service';

interface ApplicationApi {
  id: string;
  contract_id: string;
  application_number?: string | null;
  approval_acceptance_no?: string | null;
  total_quantum?: number | string | null;
  approved_quantum?: number | string | null;
  schedules?: Array<{
    from_date?: string | null;
    to_date?: string | null;
    from_time?: string | null;
    to_time?: string | null;
    quantum?: number | string | null;
  }>;
  approval_schedules?: Array<{
    schedule_from?: string | null;
    schedule_to?: string | null;
    start_time?: string | null;
    end_time?: string | null;
    mwh?: number | string | null;
    requested_mw?: number | string | null;
    accepted_mw?: number | string | null;
    is_approved?: boolean | null;
  }>;
  status: string;
}

interface ApprovedContractListRow {
  contractId: string;
  contractNumber: string;
  contractType: string;
  contractCategory: string;
  sellerName: string;
  buyerName: string;
  sellerLoa: string;
  sellerQuantum: number | null;
  buyerLoa: string;
  buyerQuantum: number | null;
  punchedQuantum: number | null;
  approvedQuantum: number | null;
  actualQuantum: number | null;
  sellerLoaRate: number | null;
  buyerLoaRate: number | null;
  sellerPenaltyApplicable: boolean;
  sellerPenaltyMode: string | null;
  sellerPenaltyFixedRate: number | null;
  sellerPenaltySlabsJson: string | null;
  buyerPenaltyApplicable: boolean;
  buyerPenaltyMode: string | null;
  buyerPenaltyFixedRate: number | null;
  buyerPenaltySlabsJson: string | null;
  periodFrom: string;
  periodTo: string;
  status: string;
}

@Component({
  standalone: true,
  selector: 'app-rea-contract-analysis-page',
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './rea-contract-analysis.page.html',
  styleUrl: './rea-contract-analysis.page.scss',
})
export class ReaContractAnalysisPage implements OnInit {
  private readonly api = inject(ApiService);
  private readonly contractsService = inject(ContractsService);
  private readonly vendorsService = inject(VendorsService);
  private readonly loaService = inject(LoaService);
  private readonly reaReportsService = inject(ReaReportsService);
  private readonly toast = inject(ToastService);

  protected readonly BarChart3Icon = BarChart3;
  protected readonly RefreshCcwIcon = RefreshCcw;

  protected readonly loading = signal(false);
  protected readonly approvedContracts = signal<ApprovedContractListRow[]>([]);

  protected readonly totalApprovedContracts = computed(() => this.approvedContracts().length);
  protected readonly totalSaleContracts = computed(
    () => this.approvedContracts().filter((item) => item.contractCategory === 'SALE-PURCHASE').length,
  );
  protected readonly totalBankingContracts = computed(
    () => this.approvedContracts().filter((item) => item.contractCategory === 'BANKING').length,
  );

  ngOnInit(): void {
    this.loadApprovedContracts();
  }

  protected refresh(): void {
    this.loadApprovedContracts();
  }

  protected formatDate(value: string): string {
    if (!value) return 'NA';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  }

  protected statusClass(status: string): string {
    const normalized = (status || '').trim().toLowerCase();
    if (normalized === 'approved' || normalized === 'active') {
      return 'status-pill status-pill-good';
    }
    return 'status-pill status-pill-neutral';
  }

  protected formatQuantum(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'NA';
    }
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  }

  protected deviationValue(row: ApprovedContractListRow): number | null {
    if (row.approvedQuantum === null || row.approvedQuantum === undefined || row.actualQuantum === null || row.actualQuantum === undefined) {
      return null;
    }
    return row.actualQuantum - row.approvedQuantum;
  }

  protected deviationDirection(row: ApprovedContractListRow): 'increase' | 'decrease' | 'neutral' {
    const deviation = this.deviationValue(row);
    if (deviation === null) {
      return 'neutral';
    }
    if (deviation > 0) {
      return 'increase';
    }
    if (deviation < 0) {
      return 'decrease';
    }
    return 'neutral';
  }

  protected deviationBarWidth(row: ApprovedContractListRow): number {
    const deviation = this.deviationValue(row);
    const approved = row.approvedQuantum;
    if (deviation === null || approved === null || approved === undefined) {
      return 0;
    }
    const baseline = Math.abs(approved) || 1;
    return Math.max(8, Math.min(100, (Math.abs(deviation) / baseline) * 100));
  }

  protected formatDeviation(row: ApprovedContractListRow): string {
    const deviation = this.deviationValue(row);
    if (deviation === null) {
      return 'NA';
    }
    const formatted = this.formatQuantum(Math.abs(deviation));
    if (deviation > 0) {
      return `+${formatted}`;
    }
    if (deviation < 0) {
      return `-${formatted}`;
    }
    return formatted;
  }

  protected approvedCostValue(row: ApprovedContractListRow): number | null {
    const rate = this.firstFiniteNumber(row.sellerLoaRate, row.buyerLoaRate);
    if (row.approvedQuantum === null || row.approvedQuantum === undefined || rate === null) {
      return null;
    }
    return row.approvedQuantum * rate;
  }

  protected actualValue(row: ApprovedContractListRow): number | null {
    const rate = this.firstFiniteNumber(row.sellerLoaRate, row.buyerLoaRate);
    if (row.actualQuantum === null || row.actualQuantum === undefined || rate === null) {
      return null;
    }
    return row.actualQuantum * rate;
  }

  protected profitLossValue(row: ApprovedContractListRow): number | null {
    const actualValue = this.actualValue(row);
    const approvedCost = this.approvedCostValue(row);
    if (actualValue === null || approvedCost === null) {
      return null;
    }
    return actualValue - approvedCost;
  }
  protected profitLossLabel(row: ApprovedContractListRow): string {
    const value = this.profitLossValue(row);
    if (value === null) {
      return 'NA';
    }
    const amount = this.formatCurrency(Math.abs(value));
    if (value > 0) {
      return `Profit ${amount}`;
    }
    if (value < 0) {
      return `Loss ${amount}`;
    }
    return `Break-even ${amount}`;
  }

  protected profitLossClass(row: ApprovedContractListRow): string {
    const value = this.profitLossValue(row);
    if (value === null || value === 0) {
      return 'metric-chip metric-chip-neutral';
    }
    return value > 0 ? 'metric-chip metric-chip-positive' : 'metric-chip metric-chip-negative';
  }

  protected penaltySummary(row: ApprovedContractListRow): string {
    const deviation = this.deviationValue(row);
    const penaltyLoa = this.penaltyLoaDetails(row);
    if (!penaltyLoa) {
      return 'NA';
    }

    const modeLabel = penaltyLoa.mode === 'quantum_wise' ? 'Quantum-wise' : 'Fixed';
    if (deviation === null || deviation >= 0) {
      return `${modeLabel} penalty`;
    }

    const shortfall = Math.abs(deviation);
    if (penaltyLoa.fixedRate !== null) {
      return `${modeLabel}: ${this.formatCurrency(shortfall * penaltyLoa.fixedRate)}`;
    }
    if (penaltyLoa.slabsJson) {
      return `${modeLabel}: as per slab`;
    }
    return `${modeLabel} penalty`;
  }

  protected penaltyDetail(row: ApprovedContractListRow): string {
    const penaltyLoa = this.penaltyLoaDetails(row);
    if (!penaltyLoa) {
      return 'No penalty';
    }
    if (penaltyLoa.fixedRate !== null) {
      return `LOA rate ${this.formatCurrency(penaltyLoa.fixedRate)}/MWh`;
    }
    if (penaltyLoa.slabsJson) {
      return 'Configured in LOA slabs';
    }
    return 'Configured in LOA';
  }

  private penaltyLoaDetails(row: ApprovedContractListRow): { mode: string | null; fixedRate: number | null; slabsJson: string | null } | null {
    if (row.sellerPenaltyApplicable) {
      return {
        mode: row.sellerPenaltyMode,
        fixedRate: row.sellerPenaltyFixedRate,
        slabsJson: row.sellerPenaltySlabsJson,
      };
    }
    if (row.buyerPenaltyApplicable) {
      return {
        mode: row.buyerPenaltyMode,
        fixedRate: row.buyerPenaltyFixedRate,
        slabsJson: row.buyerPenaltySlabsJson,
      };
    }
    return null;
  }

  private formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'NA';
    }
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(value);
  }

  private loadApprovedContracts(): void {
    this.loading.set(true);
    forkJoin({
      contracts: this.contractsService.list(),
      vendors: this.vendorsService.list(),
      loas: this.loaService.list(),
      applications: this.api.get<ApplicationApi[]>('/api/applications'),
      srpcReports: this.reaReportsService.listSaved('SRPC'),
      nrpcReports: this.reaReportsService.listSaved('NRPC'),
      nerpcReports: this.reaReportsService.listSaved('NERPC'),
    }).subscribe({
      next: ({ contracts, vendors, loas, applications, srpcReports, nrpcReports, nerpcReports }) => {
        const vendorMap = new Map((vendors || []).map((item) => [item.id, item]));
        const loaMap = new Map((loas || []).map((item) => [item.id, item]));
        const punchedQuantumByContract = this.buildPunchedQuantumByContract(applications || []);
        const approvedQuantumByContract = this.buildApprovedQuantumByContract(applications || []);
        const latestReport = this.pickLatestReaReport([...(srpcReports || []), ...(nrpcReports || []), ...(nerpcReports || [])]);
        if (!latestReport) {
          const rows = this.buildApprovedRows(
            contracts || [],
            vendorMap,
            loaMap,
            punchedQuantumByContract,
            approvedQuantumByContract,
            new Map<string, number>(),
          );
          this.approvedContracts.set(rows);
          this.loading.set(false);
          return;
        }

        this.reaReportsService.getContractAnalysis(latestReport.id).subscribe({
          next: (detail) => {
            const actualQuantumByContract = this.buildActualQuantumByContract(detail);
            const rows = this.buildApprovedRows(
              contracts || [],
              vendorMap,
              loaMap,
              punchedQuantumByContract,
              approvedQuantumByContract,
              actualQuantumByContract,
            );
            this.approvedContracts.set(rows);
            this.loading.set(false);
          },
          error: () => {
            const rows = this.buildApprovedRows(
              contracts || [],
              vendorMap,
              loaMap,
              punchedQuantumByContract,
              approvedQuantumByContract,
              new Map<string, number>(),
            );
            this.approvedContracts.set(rows);
            this.loading.set(false);
            this.toast.show('REA actual quantum could not be matched from the latest saved REA report', '', 'info');
          },
        });
      },
      error: () => {
        this.approvedContracts.set([]);
        this.loading.set(false);
        this.toast.show('Failed to load approved contract list', '', 'danger');
      },
    });
  }

  private buildApprovedRows(
    contracts: Contract[],
    vendorMap: Map<string, Vendor>,
    loaMap: Map<string, Loa>,
    punchedQuantumByContract: Map<string, number>,
    approvedQuantumByContract: Map<string, number>,
    actualQuantumByContract: Map<string, number>,
  ): ApprovedContractListRow[] {
    return (contracts || [])
      .filter((item) => this.isApprovedContract(item))
      .map((item) =>
        this.toApprovedContractRow(
          item,
          vendorMap,
          loaMap,
          punchedQuantumByContract,
          approvedQuantumByContract,
          actualQuantumByContract,
        ),
      )
      .sort((left, right) => left.contractNumber.localeCompare(right.contractNumber));
  }

  private toApprovedContractRow(
    contract: Contract,
    vendorMap: Map<string, Vendor>,
    loaMap: Map<string, Loa>,
    punchedQuantumByContract: Map<string, number>,
    approvedQuantumByContract: Map<string, number>,
    actualQuantumByContract: Map<string, number>,
  ): ApprovedContractListRow {
    const parties = contract.party_details || [];
    const sellerParty = parties.find((item) => this.resolveLoaKind(item.loa_id, loaMap) === 'sell') || parties[1] || parties[0];
    const buyerParty = parties.find((item) => this.resolveLoaKind(item.loa_id, loaMap) === 'buy') || parties[0] || parties[1];
    const sellerLoa = sellerParty?.loa_id ? loaMap.get(sellerParty.loa_id) : null;
    const buyerLoa = buyerParty?.loa_id ? loaMap.get(buyerParty.loa_id) : null;
    const sellerVendor = sellerParty?.vendor_id ? vendorMap.get(sellerParty.vendor_id) : null;
    const buyerVendor = buyerParty?.vendor_id ? vendorMap.get(buyerParty.vendor_id) : null;

    return {
      contractId: contract.id,
      contractNumber: contract.contract_number,
      contractType: contract.contract_type || 'NA',
      contractCategory: this.contractCategory(contract),
      sellerName: sellerVendor?.vendor_name || 'NA',
      buyerName: buyerVendor?.vendor_name || 'NA',
      sellerLoa: sellerLoa?.loa_number || 'NA',
      sellerQuantum: this.firstFiniteNumber(sellerLoa?.total_quantum, sellerParty?.quantum),
      buyerLoa: buyerLoa?.loa_number || 'NA',
      buyerQuantum: this.firstFiniteNumber(buyerLoa?.total_quantum, buyerParty?.quantum),
      punchedQuantum: punchedQuantumByContract.get(String(contract.id)) ?? null,
      approvedQuantum: approvedQuantumByContract.get(String(contract.id)) ?? null,
      actualQuantum: actualQuantumByContract.get(String(contract.id)) ?? null,
      sellerLoaRate: this.loaRate(sellerLoa),
      buyerLoaRate: this.loaRate(buyerLoa),
      sellerPenaltyApplicable: Boolean(sellerLoa?.penalty_applicable),
      sellerPenaltyMode: sellerLoa?.penalty_mode || null,
      sellerPenaltyFixedRate: this.toNumber(sellerLoa?.penalty_fixed_rate),
      sellerPenaltySlabsJson: sellerLoa?.penalty_slabs_json || null,
      buyerPenaltyApplicable: Boolean(buyerLoa?.penalty_applicable),
      buyerPenaltyMode: buyerLoa?.penalty_mode || null,
      buyerPenaltyFixedRate: this.toNumber(buyerLoa?.penalty_fixed_rate),
      buyerPenaltySlabsJson: buyerLoa?.penalty_slabs_json || null,
      periodFrom: contract.period_from,
      periodTo: contract.period_to,
      status: contract.status || 'NA',
    };
  }

  private loaRate(loa: Loa | null | undefined): number | null {
    return this.firstFiniteNumber(loa?.fixed_rate, loa?.mcp_value);
  }

  private resolveLoaKind(loaId: string | null | undefined, loaMap: Map<string, Loa>): 'buy' | 'sell' | 'bank' | 'unknown' {
    if (!loaId) return 'unknown';
    const category = (loaMap.get(loaId)?.category || '').toLowerCase();
    if (category.includes('sell') || category.includes('sale')) return 'sell';
    if (category.includes('buy') || category.includes('purchase')) return 'buy';
    if (category.includes('bank')) return 'bank';
    return 'unknown';
  }

  private contractCategory(contract: Contract): string {
    const savedCategory = (contract.contract_category || '').trim().toUpperCase();
    if (savedCategory) return savedCategory;
    const contractType = (contract.contract_type || '').toLowerCase();
    if (contractType.includes('bank')) return 'BANKING';
    if (contractType.includes('hybrid')) return 'HYBRID';
    return 'SALE-PURCHASE';
  }

  private isApprovedContract(contract: Contract): boolean {
    const status = (contract.status || '').trim().toLowerCase();
    return status === 'approved' || status === 'active';
  }

  private buildPunchedQuantumByContract(applications: ApplicationApi[]): Map<string, number> {
    const totals = new Map<string, number>();
    for (const app of applications || []) {
      const status = (app.status || '').trim().toLowerCase();
      if (status !== 'punched') {
        continue;
      }
      const contractId = String(app.contract_id || '').trim();
      if (!contractId) {
        continue;
      }
      const quantum =
        this.quantumFromSchedules(
          app.schedules || [],
          (schedule) => schedule.from_date,
          (schedule) => schedule.to_date,
          (schedule) => schedule.from_time,
          (schedule) => schedule.to_time,
          (schedule) => schedule.quantum,
        ) ?? this.firstFiniteNumber(app.total_quantum, app.approved_quantum);
      if (!quantum) {
        continue;
      }
      totals.set(contractId, (totals.get(contractId) || 0) + quantum);
    }
    return totals;
  }

  private buildApprovedQuantumByContract(applications: ApplicationApi[]): Map<string, number> {
    const totals = new Map<string, number>();
    for (const app of applications || []) {
      const contractId = String(app.contract_id || '').trim();
      if (!contractId) {
        continue;
      }

      const quantum = this.approvedQuantumFromSchedules(app);
      if (!quantum) {
        continue;
      }
      totals.set(contractId, (totals.get(contractId) || 0) + quantum);
    }
    return totals;
  }

  private approvedQuantumFromSchedules(app: ApplicationApi): number | null {
    const values = (app.approval_schedules || [])
      .filter((row) => row.is_approved !== false)
      .map((row) => this.toNumber(row.mwh))
      .filter((value): value is number => value !== null);

    if (!values.length) {
      return null;
    }

    return values.reduce((sum, value) => sum + value, 0);
  }

  private quantumFromSchedules<T>(
    schedules: T[],
    getFromDate: (schedule: T) => string | null | undefined,
    getToDate: (schedule: T) => string | null | undefined,
    getFromTime: (schedule: T) => string | null | undefined,
    getToTime: (schedule: T) => string | null | undefined,
    getQuantum: (schedule: T) => number | string | null | undefined,
  ): number | null {
    let total = 0;
    let hasValidSchedule = false;

    for (const schedule of schedules || []) {
      const fromDate = this.parseDateOnly(getFromDate(schedule));
      const toDate = this.parseDateOnly(getToDate(schedule) || getFromDate(schedule));
      const timeDifference = this.timeDifferenceHours(getFromTime(schedule) || '00:00', getToTime(schedule) || '24:00');
      const quantum = this.toNumber(getQuantum(schedule));

      if (!fromDate || !toDate || !timeDifference || quantum === null) {
        continue;
      }

      const dayDifference = this.inclusiveDayCount(fromDate, toDate);
      if (!dayDifference) {
        continue;
      }

      total += dayDifference * timeDifference * quantum * 250;
      hasValidSchedule = true;
    }

    return hasValidSchedule ? total : null;
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    if (!value) return null;
    const normalized = value.trim().split('T')[0].replace(/\//g, '-');

    let parts = normalized.split('-').map((part) => Number(part));
    if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) {
      return null;
    }

    let year = 0;
    let month = 0;
    let day = 0;

    if (String(parts[0]).length === 4) {
      [year, month, day] = parts;
    } else if (String(parts[2]).length === 4) {
      [day, month, year] = parts;
    } else {
      return null;
    }

    if (!year || !month || !day) return null;
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private inclusiveDayCount(start: Date, end: Date): number {
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
  }

  private timeDifferenceHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const fromMinutes = this.parseTimeToMinutes(fromTime);
    const toMinutes = this.parseTimeToMinutes(toTime);
    if (fromMinutes === null || toMinutes === null || toMinutes <= fromMinutes) return 0;
    return (toMinutes - fromMinutes) / 60;
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value) return null;
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes >= 60) return null;
    if (hours === 24 && minutes === 0) return 24 * 60;
    if (hours < 0 || hours > 23) return null;
    return hours * 60 + minutes;
  }

  private toNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private firstFiniteNumber(...values: Array<string | number | null | undefined>): number | null {
    for (const value of values) {
      const parsed = this.toNumber(value);
      if (parsed !== null) {
        return parsed;
      }
    }
    return null;
  }

  private pickLatestReaReport(reports: ReaReport[]): ReaReport | null {
    if (!reports.length) {
      return null;
    }
    return [...reports].sort((left, right) => {
      const leftTime = new Date(left.last_refetched_at || left.created_at).getTime();
      const rightTime = new Date(right.last_refetched_at || right.created_at).getTime();
      return rightTime - leftTime;
    })[0] || null;
  }

  private buildActualQuantumByContract(detail: ReaContractAnalysisDetail): Map<string, number> {
    const totals = new Map<string, number>();

    for (const row of detail.rows || []) {
      const contractId = String(row.contract_id || '').trim();
      if (!contractId) {
        continue;
      }
      const actualMwh = this.toNumber(row.rea_actual_mwh);
      if (actualMwh === null) {
        continue;
      }
      totals.set(contractId, (totals.get(contractId) || 0) + actualMwh);
    }
    return totals;
  }
}
